import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

/**
 * Story 83.2 — BrandingProvider
 *
 * Reads per-tenant branding from /api/v1/branding and injects CSS
 * variables + custom CSS into a <style> tag at the root. Components
 * that want to reflect branding (e.g. logo in NavBar) can consume
 * useBranding().
 */

interface BrandingData {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  fontFamily: string | null;
  customCss: string | null;
  customDomain: string | null;
  themeTokens?: {
    light?: Record<string, string>;
    dark?: Record<string, string>;
  } | null;
}

interface BrandingContextValue {
  branding: BrandingData | null;
  loading: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  loading: false,
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const tokens = useAuthStore((s) => s.tokens);

  // Keyed on `tokens`, exactly like MaintenanceBanner, because /branding is
  // tenant-scoped and sits behind the authenticated group. This provider mounts
  // above the router, so on the load that follows an SSO round-trip it used to
  // fire once with no token, take a 401, swallow it in the catch below, and —
  // with an empty dependency array — never ask again: the tenant kept the
  // default theme until a manual refresh, and every login logged a console
  // error. Waiting for the token also re-fetches on tenant switch.
  useEffect(() => {
    if (!tokens) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<BrandingData>('/branding')
      .then(({ data }) => {
        if (cancelled) return;
        setBranding(data);
        applyBranding(data, styleRef);
      })
      .catch(() => {
        // Non-fatal — default theme remains
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  return (
    <BrandingContext.Provider value={{ branding, loading }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

function applyBranding(
  data: BrandingData,
  styleRef: React.MutableRefObject<HTMLStyleElement | null>,
) {
  // Build CSS variable overrides
  const vars: string[] = [];
  if (data.primaryColor) {
    vars.push(`--color-primary: ${data.primaryColor};`);
  }
  if (data.accentColor) {
    vars.push(`--color-accent: ${data.accentColor};`);
  }
  if (data.fontFamily) {
    vars.push(`--font-family: ${data.fontFamily};`);
  }

  // Merge themeTokens light mode
  if (data.themeTokens?.light) {
    for (const [key, val] of Object.entries(data.themeTokens.light)) {
      vars.push(`${key}: ${val};`);
    }
  }

  const cssVarBlock =
    vars.length > 0 ? `:root {\n  ${vars.join('\n  ')}\n}\n` : '';

  const customCss = data.customCss ?? '';

  const fullCss = cssVarBlock + customCss;

  if (!fullCss.trim()) {
    // Nothing to inject
    if (styleRef.current) {
      styleRef.current.remove();
      styleRef.current = null;
    }
    return;
  }

  // Reuse or create the style tag
  if (!styleRef.current) {
    const el = document.createElement('style');
    el.setAttribute('data-kairos-branding', 'true');
    document.head.appendChild(el);
    styleRef.current = el;
  }
  styleRef.current.textContent = fullCss;

  // Update favicon if provided
  if (data.faviconUrl) {
    const link =
      (document.querySelector("link[rel~='icon']") as HTMLLinkElement) ||
      document.createElement('link');
    link.rel = 'icon';
    link.href = data.faviconUrl;
    if (!link.parentNode) document.head.appendChild(link);
  }
}
